<script setup lang="ts">
import { ref, onMounted } from 'vue';
import sanitizeHtml from 'sanitize-html';

interface StepItem {
  label: string;
  title: string;
  text: string;
  open?: boolean;
}

const props = defineProps<{
  section: {
    heading: string;
    text: string;
    steps: StepItem[];
    button: {
      url: string;
      title: string;
      target: string;
    };
  };
}>();

const openStates = ref(new Map<string, boolean>());

onMounted(() => {
  props.section.steps.forEach((step) => {
    openStates.value.set(step.title, !!step.open);
  });
});

const toggleDropdown = (title: string) => {
  openStates.value.forEach((_, key) => {
    openStates.value.set(
      key,
      key === title ? !openStates.value.get(key) : false
    );
  });

  if (openStates.value.get(title)) {
    const element = document.querySelector(`[data-title="${title}"]`);
    if (element) {
      setTimeout(() => {
        const headerHeight =
          document.querySelector('#header')?.clientHeight || 0;
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - headerHeight,
          behavior: 'smooth',
        });
      }, 500);
    }
  }
};
</script>

<template>
  <section class="relative z-10 bg-grad-bluepink pb-16 pt-15 md:py-28">
    <div class="container lg:-mb-1.5 lg:pt-4">
      <div class="mx-auto mb-8 md:mb-[108px] md:text-center">
        <h2 v-if="section.heading" class="mb-1 md:mb-4">
          {{ section.heading }}
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-if="section.text"
          class="mx-auto mb-8 max-w-4xl text-xl"
          v-html="sanitizeHtml(section.text)"
        />
      </div>
      <ul
        class="mb-11 gap-10 md:mb-9 md:grid md:min-h-[340px] md:grid-cols-2 md:grid-cols-3 lg:gap-x-20 xl:gap-x-[139px] xl:px-15"
      >
        <template v-for="(step, index) in section.steps" :key="index">
          <li
            v-if="step.label || step.title || step.text"
            class="border-b border-dotted border-current py-6 first:pt-0 md:border-none md:p-0"
            :data-title="step.title"
          >
            <strong v-if="step.label" class="subtitle mb-4 block md:mb-3.5">
              {{ step.label }}
            </strong>
            <h3
              v-if="step.title"
              class="title relative mb-1 pr-5 md:mb-4.5 md:pr-0"
              @click.prevent="toggleDropdown(step.title)"
            >
              {{ step.title }}
              <svg
                class="absolute right-0 top-3 h-3 w-3 transition-all duration-300 md:hidden"
                :class="{ '-rotate-180': !openStates.get(step.title) }"
              >
                <use href="/icons/icons.svg#arrow" />
              </svg>
            </h3>
            <div
              class="grid transition-all duration-500 md:visible md:block md:grid-rows-[1fr]"
              :class="{
                'visible grid-rows-[1fr]': openStates.get(step.title),
                'invisible grid-rows-[0fr]': !openStates.get(step.title),
              }"
            >
              <div
                v-if="step.text"
                class="overflow-hidden text-gray"
                v-html="sanitizeHtml(step.text)"
              />
            </div>
          </li>
        </template>
      </ul>
      <div v-if="section.button" class="md:text-center">
        <ButtonLink v-bind="section.button" icon="arrow-r" />
      </div>
    </div>
  </section>
</template>

<style lang="scss"></style>
